import {
  cameraApi,
  useRebootCameraMutation,
  useRestartCameraMutation,
} from "@/core/cameraApi";
import i18n from "@/core/i18n";
import { useAppDispatch } from "@/hooks/redux";
import { useCameraApiRetry } from "@/modules/camera-stream";
import { bumpPipeline } from "@/modules/camera-stream/services/cameraPipelineGenerationSlice";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useVideoCapture } from "../context/VideoCaptureContext";

/**
 * How the camera can be brought back:
 *
 * - `service` restarts the camera service — the pipeline is torn down and
 *   rebuilt, the stream reconnects in a few seconds. Fixes a frozen preview.
 * - `device` reboots the hardware itself. Everything on the device goes away
 *   for about a minute, including the camera API answering these requests.
 */
export type CameraRestartScope = "service" | "device";

/** Matches the device's own boot time; the wait ends early if it answers sooner. */
const DEVICE_REBOOT_SECONDS = 60;

// The device keeps answering for a moment after accepting the reboot, so a
// probe fired straight away would "find" the camera that is about to vanish
// and end the wait before the reboot has even started.
const REBOOT_PROBE_GRACE_MS = 15_000;
const REBOOT_PROBE_INTERVAL_MS = 3_000;

/**
 * A rejected request that never reached an HTTP status. From the browser this
 * is indistinguishable from — and is the usual shape of — a reboot that landed:
 * the device drops the connection while it goes down. An HTTP status means the
 * camera answered *and* refused, which is the only real failure here.
 */
const isTransportFailure = (error: unknown) => {
  const status = (error as { status?: unknown } | undefined)?.status;
  return status === "FETCH_ERROR" || status === "TIMEOUT_ERROR";
};

export interface CameraRestartState {
  /** Which restart is running, or null when idle. */
  scope: CameraRestartScope | null;
  /** Restart request in flight — the stream is reconnecting. */
  isRestartingService: boolean;
  /** Device is down for its reboot window. */
  isRebooting: boolean;
  /** Seconds left of the reboot window; 0 unless `isRebooting`. */
  rebootSecondsLeft: number;
  /** True while either restart is running. */
  isBusy: boolean;
  restart: (scope: CameraRestartScope) => Promise<void>;
}

export interface UseCameraRestartOptions {
  mxid: string | null;
  streamName: string | null;
  /** Runs before either restart — used to stop interval shooting. */
  onBeforeRestart?: () => void;
}

/**
 * Drives both restart scopes and the UI state they need: the sidebar button
 * (`RestartCameraButton`) and the reboot overlay on the live preview
 * (`CameraDisplay`) read the same object, so they can never disagree about
 * what the camera is doing.
 */
export const useCameraRestart = ({
  mxid,
  streamName,
  onBeforeRestart,
}: UseCameraRestartOptions): CameraRestartState => {
  const { t } = useTranslation("capture");
  const dispatch = useAppDispatch();
  const retryCameraApi = useCameraApiRetry();
  const { isRecording, stopAndDiscardRecording } = useVideoCapture();
  const [restartCamera] = useRestartCameraMutation();
  const [rebootCamera] = useRebootCameraMutation();

  const [scope, setScope] = useState<CameraRestartScope | null>(null);
  const [rebootSecondsLeft, setRebootSecondsLeft] = useState(0);

  // Re-runs the queries the live view depends on and forces the shared
  // WebRTC connection to rebuild against the pipeline that just came up.
  const reconnect = useCallback(() => {
    retryCameraApi();
    if (mxid && streamName) dispatch(bumpPipeline({ mxid, streamName }));
  }, [dispatch, mxid, streamName, retryCameraApi]);

  // The reboot window: count down, and poll the camera so the wait ends as
  // soon as it is back rather than always running the full minute.
  useEffect(() => {
    if (scope !== "device") return;

    const startedAt = Date.now();
    let done = false;
    let probing = false;
    let lastProbeAt = 0;

    const finish = (cameraAnswered: boolean) => {
      if (done) return;
      done = true;
      clearInterval(timer);
      setScope(null);
      setRebootSecondsLeft(0);
      reconnect();
      // i18n.t, not t: a language switch must not restart the reboot countdown.
      if (cameraAnswered) toast.success(i18n.t("capture:restart.backOnline"));
    };

    const timer = setInterval(() => {
      const elapsedMs = Date.now() - startedAt;
      setRebootSecondsLeft(
        Math.max(0, DEVICE_REBOOT_SECONDS - Math.floor(elapsedMs / 1000)),
      );

      if (elapsedMs >= DEVICE_REBOOT_SECONDS * 1000) {
        finish(false);
        return;
      }

      if (elapsedMs < REBOOT_PROBE_GRACE_MS || probing) return;
      if (elapsedMs - lastProbeAt < REBOOT_PROBE_INTERVAL_MS) return;

      probing = true;
      lastProbeAt = elapsedMs;
      void dispatch(
        cameraApi.endpoints.listCameras.initiate(undefined, {
          forceRefetch: true,
          subscribe: false,
        }),
      )
        .then((result) => {
          if (result.isSuccess) finish(true);
        })
        .finally(() => {
          probing = false;
        });
    }, 1000);

    return () => {
      done = true;
      clearInterval(timer);
    };
  }, [scope, dispatch, reconnect]);

  const restart = useCallback(
    async (next: CameraRestartScope) => {
      if (!mxid || scope) return;

      onBeforeRestart?.();
      if (isRecording) await stopAndDiscardRecording();

      if (next === "service") {
        setScope("service");
        try {
          await restartCamera({ mxid }).unwrap();
          if (streamName) dispatch(bumpPipeline({ mxid, streamName }));
          toast.success(t("restart.restarted"));
        } catch {
          toast.error(t("restart.restartFailed"));
        } finally {
          setScope(null);
        }
        return;
      }

      try {
        await rebootCamera({ mxid }).unwrap();
      } catch (error) {
        if (!isTransportFailure(error)) {
          toast.error(t("restart.rebootFailed"));
          return;
        }
      }
      setRebootSecondsLeft(DEVICE_REBOOT_SECONDS);
      setScope("device");
    },
    [
      dispatch,
      isRecording,
      mxid,
      onBeforeRestart,
      rebootCamera,
      restartCamera,
      scope,
      stopAndDiscardRecording,
      streamName,
      t,
    ],
  );

  return {
    scope,
    isRestartingService: scope === "service",
    isRebooting: scope === "device",
    rebootSecondsLeft,
    isBusy: scope !== null,
    restart,
  };
};
